import {
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Select,
  SimpleGrid,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';

import React, { useEffect, useState } from 'react';
import Card from 'components/card/Card';
import BackButton from 'components/BackButton';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useLocation, useNavigate } from 'react-router-dom';
import { createAPIEndPoint } from 'config/api/api';
import toast from 'react-hot-toast';
import Loader from 'loaders/Loader';
import { logoutUser } from 'utils';
import moment from 'moment';
import { formatUSPhoneNumber } from 'utils';

const columnHelper = createColumnHelper();

export default function PatientDetails(props) {
  const navigate = useNavigate();
  const location = useLocation();
  // const { id } = location.state;
  const pathParts = location.pathname.split('/');
  const id = pathParts[pathParts.length - 1];
  const { ...rest } = props;
  // Chakra Color Mode
  const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = 'gray.400';
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const appointmentsData = [
    { date: '02/06/2025', time: '04:22 PM', details: 'Emergency' },
  ];

  // Options Table Columns
  const appointmentsColumns = [
    columnHelper.accessor('date', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Date
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('time', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Time
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          ${info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor('details', {
      header: () => (
        <Text fontSize="12px" color="gray.400" fontWeight="bold">
          Details
        </Text>
      ),
      cell: (info) => (
        <Text color={textColor} fontSize="sm" fontWeight="700">
          ${info.getValue()}
        </Text>
      ),
    }),
  ];

  const appointmentHistory = useReactTable({
    data: appointmentsData,
    columns: appointmentsColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState({});

  const getPatient = async () => {
    try {
      setLoading(true);
      const response = await createAPIEndPoint('patient').fetchById(`/${id}`);
      const patient = response.data;

      setPatient(patient);

      setLoading(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || 'Error fetching patient details',
      );
      if (error?.response?.data?.error.includes('Bearer token')) {
        logoutUser(navigate);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    getPatient();
  }, [id]);

  return (
    <>
      {' '}
      {loading && <Loader />}
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <BackButton />
        {/* Main Fields */}
        <Grid
          templateColumns={{
            base: '1fr',
            lg: '1fr',
          }}
          templateRows={{
            base: 'repeat(2, 1fr)',
            lg: '1fr',
          }}
          gap={{ base: '20px', xl: '20px' }}
        >
          <Card mb={{ base: '0px', '2xl': '20px' }} {...rest}>
            <Text
              color={textColorPrimary}
              fontWeight="bold"
              fontSize="2xl"
              mt="8px"
              mb="24px"
            >
              General Information
            </Text>

            <SimpleGrid columns={{ base: 2, md: 3 }} spacing={8}>
              {/* Name */}
              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Name:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  {patient.name || <>N/A</>}
                </Text>
              </Box>

              {/* Email */}
              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Email:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  {patient?.email?.toLowerCase() || <>N/A</>}
                </Text>
              </Box>

              {/* Phone */}
              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Phone:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  {formatUSPhoneNumber(patient.phone) || <>N/A</>}
                </Text>
              </Box>

              {/* Date */}
              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Date of birth:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  {patient.date_of_birth
                    ? moment(patient.date_of_birth).format('MM/DD/YYYY')
                    : 'N/A'}
                </Text>
              </Box>

              {/* Address */}
              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Address:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  {patient.address || <>N/A</>}
                </Text>
              </Box>

              {/* State */}
              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  State:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  {patient.state || <>N/A</>}
                </Text>
              </Box>

              {/* Insurance Provider */}
              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Insurance Provider:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  {patient.insurance_name || <>N/A</>}
                </Text>
              </Box>

              {/* Insurance No */}
              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Insurance / Policy No:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  {patient.insurance_no || <>N/A</>}
                </Text>
              </Box>

              {/* Plan Name */}
              {/* <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Plan Name:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  N/A
                </Text>
              </Box> */}

              {/* Plan Type */}
              {/* <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Plan Type:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  N/A
                </Text>
              </Box> */}

              {/* Member ID */}
              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Member ID:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  {patient?.subscriber_id || "N/A"}
                </Text>
              </Box>

              <Box>
                <Text fontWeight="semibold" color={textColorPrimary}>
                  Created Date:
                </Text>
                <Text color={textColorSecondary} fontSize="md">
                  {patient.created_at
                    ? moment(patient.created_at).format('MM/DD/YYYY')
                    : 'N/A'}
                </Text>
              </Box>

              {/* <Box>
                <Button fontWeight="500" colorScheme="blue" size="md">
                  Add Insurance
                </Button>
              </Box> */}
            </SimpleGrid>
          </Card>
          {/* <Card mb={{ base: '0px', '2xl': '20px' }} {...rest}>
            <Text
              color={textColorPrimary}
              fontWeight="bold"
              fontSize="2xl"
              mt="10px"
              mb="4px"
            >
              Appointment History
            </Text>
            <Table variant="striped" color="gray.500" mb="24px" mt="12px">
              <Thead>
                {appointmentHistory.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <Th
                        key={header.id}
                        colSpan={header.colSpan}
                        pe="10px"
                        borderColor={borderColor}
                      >
                        <Flex justifyContent="space-between" align="center">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </Flex>
                      </Th>
                    ))}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {appointmentHistory.getRowModel().rows.map((row) => (
                  <Tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <Td
                        key={cell.id}
                        fontSize="14px"
                        borderColor="transparent"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Card>
          <Card mb={{ base: '0px', '2xl': '20px' }} {...rest}>
            <Text
              color={textColorPrimary}
              fontWeight="bold"
              fontSize="xl"
              mt="10px"
              mb="4px"
            >
              Appointment Files{' '}
            </Text>
            <Divider my={3} />
            <Text color={textColorSecondary} fontSize="md">
              No files uploaded for this appointment.{' '}
            </Text>
          </Card> */}
        </Grid>
      </Box>
    </>
  );
}
